import { cx } from "@its-engineering-and-development/ui-foundation/utils";
import { Spinner } from "@app/components/base/spinner/spinner";

export const LOADING_STATE_VARIANT = {
    /** Centered block that reserves vertical space in the normal flow. */
    Block: "block",
    /** Absolutely positioned overlay covering its nearest positioned ancestor. */
    Overlay: "overlay",
    /** Fixed overlay covering the entire viewport. */
    Fullscreen: "fullscreen",
} as const;

export type LoadingStateVariant = (typeof LOADING_STATE_VARIANT)[keyof typeof LOADING_STATE_VARIANT];

const CONTAINER_STYLES: Record<LoadingStateVariant, string> = {
    [LOADING_STATE_VARIANT.Block]: "flex min-h-[50vh] items-center justify-center",
    [LOADING_STATE_VARIANT.Overlay]: "absolute inset-0 z-10 flex items-center justify-center bg-white/80",
    [LOADING_STATE_VARIANT.Fullscreen]: "fixed inset-0 z-50 flex items-center justify-center bg-gray-50",
};

interface LoadingStateProps {
    /** Accessible name announced to assistive tech via `role="status"`. */
    label: string;
    /** Optional message rendered under the spinner. */
    message?: string;
    /** Layout of the loading container. Defaults to `block`. */
    variant?: LoadingStateVariant;
    /** Additional classes applied to the container. */
    className?: string;
}

/**
 * Reusable loading state: a centered spinner with an accessible status role and
 * an optional message. Use `variant` to switch between an in-flow block, an
 * overlay over a positioned parent, or a full-screen overlay.
 */
export function LoadingState({ label, message, variant = LOADING_STATE_VARIANT.Block, className }: LoadingStateProps) {
    return (
        <div className={cx(CONTAINER_STYLES[variant], message && "flex-col gap-3", className)} role="status" aria-label={label}>
            <Spinner className="h-8 w-8 text-blue-600" />
            {message && <p className="text-sm text-gray-500">{message}</p>}
        </div>
    );
}
